import {
  Dialog,
  type DialogProps,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useGetProfileTypes } from "../../../api/profile";
import Button from "../../../shared/components/button/Button";
import BackdropLoader from "../../../shared/components/backdrop-loader/BackdropLoader";
import { noLeadingWhitespaceRegExpression } from "../../../shared/constants";
import {
  ErrorTypography,
  RowDisplayRadioGroup,
} from "../../../shared/styles/common-styles";
import { businessProfiles, searchTypes } from "../../aml/aml.constant";
import { useSession } from "../../aml/aml-check/AmlCheckContext";
import type { CreateProfileModalFormValues } from "./buildCreateProfileModalPayload";
import {
  getDefaultLinkTarget,
  isCreateDisabledForLinkType,
} from "./createProfileModal.utils";
import { CreateProfileType } from "../types/createProfile.type";
import { useCreateProfileModal } from "./useCreateProfileModal";

const DialogHeader = styled(DialogTitle)`
  background: ${(props) => props.theme.greyBackground};
  padding: 10px 24px;
`;

/* eslint-disable no-unused-vars -- enum members are accessed via CreateProfileModalVariant.<member> */
export enum CreateProfileModalVariant {
  profilesPage = "profilesPage",
  screeningPage = "screeningPage",
  hitDetailPage = "hitDetailPage",
}
/* eslint-enable no-unused-vars */

export interface CreateProfileModalValues extends CreateProfileModalFormValues {
  nameAlreadyExists?: string;
}

interface CreateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  variant: CreateProfileModalVariant;
  searchType?: string;
  initialValues?: CreateProfileModalValues;
  /** Persists draft values when the modal closes (AML toggle retention). */
  onPersistValues?: (_values: CreateProfileModalValues) => void;
  onSuccess: (_result: {
    profileId: string;
    profileType: CreateProfileType;
  }) => void | Promise<void>;
}

interface ModalFormFields extends CreateProfileModalFormValues {
  profileType: CreateProfileType;
}

const defaultFormValues: ModalFormFields = {
  profileName: "",
  profileType: CreateProfileType.none,
  legalName: "",
  tradingName: "",
};

const CreateProfileModal = ({
  isOpen,
  onClose,
  variant,
  searchType,
  initialValues,
  onPersistValues,
  onSuccess,
}: CreateProfileModalProps): JSX.Element => {
  const { t: translate } = useTranslation(["profile", "common", "aml"]);
  const amlSessionName = useSession()?.sessionState?.amlDetails?.name;
  const { data: profileTypesData } = useGetProfileTypes();
  const [nameConflictError, setNameConflictError] = useState("");
  const [isEntityTypeOpen, setIsEntityTypeOpen] = useState(false);
  const [isEntityTypeFocused, setIsEntityTypeFocused] = useState(false);
  const previousProfileTypeRef = useRef<CreateProfileType>(
    CreateProfileType.none
  );

  const showLinkTarget = variant !== CreateProfileModalVariant.profilesPage;
  const isHitDetail = variant === CreateProfileModalVariant.hitDetailPage;
  const linkLabelKey = isHitDetail
    ? "profile.create.addMatchTo"
    : "aml:addBusinessToSearches.addSearchTo";
  const linkErrorKey = isHitDetail
    ? "profile.create.matchLinkErrorMessage"
    : "profile.create.searchLinkErrorMessage";

  const { createProfile, isLoading } = useCreateProfileModal({
    onNameConflict: (message) => {
      setNameConflictError(message);
    },
  });

  const { control, handleSubmit, reset, watch, clearErrors, setValue, getValues } =
    useForm<ModalFormFields>({
      mode: "onSubmit",
      defaultValues: defaultFormValues,
    });

  const watchedProfileType = watch("profileType");
  const showLegalName =
    watchedProfileType !== CreateProfileType.none && !!watchedProfileType;
  const showTradingName = watchedProfileType === CreateProfileType.soleTrader;
  const isIndividualSearch = searchType === searchTypes.individual;
  const isBusinessProfileType = businessProfiles.includes(watchedProfileType);
  const isIndividualProfileType =
    watchedProfileType === CreateProfileType.individual ||
    watchedProfileType === CreateProfileType.soleTrader;
  const showIndividualLinkTarget = isIndividualProfileType;
  const showBusinessLinkTarget = isBusinessProfileType;
  const isCreateDisabled =
    showLinkTarget &&
    isCreateDisabledForLinkType(watchedProfileType, searchType);

  useEffect(() => {
    if (!isOpen) {
      setIsEntityTypeOpen(false);
      setIsEntityTypeFocused(false);
      return;
    }

    const sessionProfileName = isHitDetail ? amlSessionName?.trim() : undefined;
    const initialProfileName = initialValues?.profileName?.trim();
    const mergedValues: ModalFormFields = {
      profileName: initialProfileName || sessionProfileName || "",
      profileType: initialValues?.profileType ?? CreateProfileType.none,
      legalName: initialValues?.legalName ?? "",
      tradingName: initialValues?.tradingName ?? "",
    };

    reset(mergedValues);
    setNameConflictError(initialValues?.nameAlreadyExists ?? "");
    previousProfileTypeRef.current = mergedValues.profileType;
    // Only hydrate when the modal opens; avoid resetting while the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reset]);

  const persistDraftValues = useCallback((): void => {
    if (!onPersistValues) {
      return;
    }

    const currentValues = getValues();
    onPersistValues({
      profileName: currentValues.profileName,
      profileType: currentValues.profileType,
      legalName: currentValues.legalName,
      tradingName: currentValues.tradingName,
      nameAlreadyExists: nameConflictError,
    });
  }, [getValues, nameConflictError, onPersistValues]);

  const handleClose = useCallback((): void => {
    persistDraftValues();
    onClose();
  }, [onClose, persistDraftValues]);

  const handleDialogOnClose: DialogProps["onClose"] = useCallback(
    (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
      if (reason === "backdropClick" || reason === "escapeKeyDown") {
        return;
      }
      handleClose();
    },
    [handleClose]
  );

  const handleProfileTypeChange = useCallback(
    (profileType: CreateProfileType): void => {
      const shouldPrepopulateLegalName =
        previousProfileTypeRef.current === CreateProfileType.none &&
        profileType !== CreateProfileType.none;

      setValue("profileType", profileType);
      previousProfileTypeRef.current = profileType;
      setNameConflictError("");

      if (shouldPrepopulateLegalName) {
        setValue("legalName", getValues("profileName"));
      }

      if (profileType !== CreateProfileType.soleTrader) {
        setValue("tradingName", "");
        clearErrors("tradingName");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- react-hook-form setters are stable
    []
  );

  const onSubmit = useCallback(
    async (data: ModalFormFields) => {
      clearErrors();
      setNameConflictError("");

      const result = await createProfile({
        profileName: data.profileName.trim(),
        profileType: data.profileType,
        legalName: data.legalName.trim(),
        tradingName: data.tradingName?.trim(),
      });

      if (result?.profileId) {
        await onSuccess(result);
      }
    },
    [clearErrors, createProfile, onSuccess]
  );

  const textFieldRules = {
    required: translate("profile:profile.validationMsg.profileNameRequired"),
    maxLength: {
      value: 150,
      message: translate("profile:profile.validationMsg.profileNameMaxLength"),
    },
    pattern: {
      value: noLeadingWhitespaceRegExpression,
      message: translate("common:validationMsg.whiteSpaceError"),
    },
  };

  const legalNameRules = {
    required: translate("profile:profile.create.validationMsg.nameRequired"),
    maxLength: {
      value: 150,
      message: translate("profile:profile.create.validationMsg.nameMaxLength"),
    },
    pattern: {
      value: noLeadingWhitespaceRegExpression,
      message: translate("common:validationMsg.whiteSpaceError"),
    },
  };

  const tradingNameRules = useMemo(
    () =>
      showTradingName
        ? {
            required: translate(
              "profile:profile.create.validationMsg.tradingNameRequired"
            ),
            maxLength: {
              value: 150,
              message: translate(
                "profile:profile.create.validationMsg.tradingNameMaxLength"
              ),
            },
            pattern: {
              value: noLeadingWhitespaceRegExpression,
              message: translate("common:validationMsg.whiteSpaceError"),
            },
          }
        : {},
    [showTradingName, translate]
  );

  const profileTypesMenuItems = (): JSX.Element[] | null => {
    if (!profileTypesData) {
      return null;
    }
    return Object.values(profileTypesData).map((type: string) => (
      <MenuItem value={type} key={type} aria-label={`${type} type`}>
        {translate(`profile.type.${type}`)}
      </MenuItem>
    ));
  };

  const defaultLinkTarget = getDefaultLinkTarget(
    watchedProfileType,
    searchType
  );

  const entityTypeLabel = translate("profile.create.entityType");
  const entityTypePlaceholder = translate("profile.create.enterEntityType");
  const isEntityTypeActive = isEntityTypeOpen || isEntityTypeFocused;

  const handleEntityTypeOpen = useCallback(() => {
    setIsEntityTypeOpen(true);
  }, []);

  const handleEntityTypeClose = useCallback(() => {
    setIsEntityTypeOpen(false);
  }, []);

  const handleEntityTypeFocus = useCallback(() => {
    setIsEntityTypeFocused(true);
  }, []);

  const handleEntityTypeBlur = useCallback(() => {
    setIsEntityTypeFocused(false);
  }, []);

  const renderEntityTypeValue = useCallback(
    (selected: string) => {
      if (selected) {
        return translate(`profile.type.${selected}`);
      }
      if (isEntityTypeActive) {
        return entityTypePlaceholder;
      }
      return "";
    },
    [entityTypePlaceholder, isEntityTypeActive, translate]
  );

  return (
    <Dialog
      open={isOpen}
      onClose={handleDialogOnClose}
      disableEscapeKeyDown
      fullWidth
      maxWidth="sm"
      aria-labelledby="create-profile-modal-dialog"
    >
      <DialogHeader id="create-profile-modal-dialog">
        {translate("profile.create.createManualProfileBtn")}
        <IconButton
          onClick={handleClose}
          aria-label="close-button"
          sx={{ position: "absolute", right: 12, top: 6 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ padding: "24px !important" }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="profileName"
                control={control}
                rules={textFieldRules}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={translate("profile.create.profileName")}
                    placeholder={String(
                      translate("profile.create.enterProfileName")
                    )}
                    size="small"
                    fullWidth
                    required
                    inputProps={{ "aria-label": "profile-name-input" }}
                    onChange={(event) => {
                      field.onChange(event);
                      setNameConflictError("");
                    }}
                    error={!!fieldState.error || nameConflictError.length > 0}
                    helperText={
                      fieldState.error?.message ?? nameConflictError ?? ""
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="profileType"
                control={control}
                rules={{
                  validate: (value) =>
                    value !== CreateProfileType.none ||
                    (translate("profile.create.enterEntityType") ?? false),
                }}
                render={({ field, fieldState }) => {
                  const selectedEntityType =
                    field.value &&
                    profileTypesData &&
                    Object.values(profileTypesData).includes(field.value)
                      ? field.value
                      : "";
                  const shouldShrinkEntityTypeLabel =
                    Boolean(selectedEntityType) || isEntityTypeActive;

                  return (
                    <FormControl
                      fullWidth
                      size="small"
                      error={!!fieldState.error}
                    >
                      <InputLabel
                        id="create-profile-type-label"
                        shrink={shouldShrinkEntityTypeLabel}
                      >
                        {entityTypeLabel}
                      </InputLabel>
                      <Select
                        {...field}
                        labelId="create-profile-type-label"
                        label={entityTypeLabel}
                        displayEmpty
                        inputProps={{ "aria-label": "select-profile-type" }}
                        value={selectedEntityType}
                        onOpen={handleEntityTypeOpen}
                        onClose={handleEntityTypeClose}
                        onFocus={handleEntityTypeFocus}
                        onBlur={() => {
                          handleEntityTypeBlur();
                          field.onBlur();
                        }}
                        onChange={(event) => {
                          const nextType = event.target
                            .value as CreateProfileType;
                          field.onChange(nextType);
                          handleProfileTypeChange(nextType);
                        }}
                        renderValue={renderEntityTypeValue}
                        sx={
                          !selectedEntityType && isEntityTypeActive
                            ? { color: "text.secondary" }
                            : undefined
                        }
                      >
                        {profileTypesMenuItems()}
                      </Select>
                      {fieldState.error && (
                        <FormHelperText>
                          {fieldState.error.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  );
                }}
              />
            </Grid>
            {showLegalName && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <Controller
                  name="legalName"
                  control={control}
                  rules={legalNameRules}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={translate("profile.create.legalName")}
                      placeholder={String(
                        translate("profile.create.enterLegalName")
                      )}
                      size="small"
                      fullWidth
                      required
                      inputProps={{ "aria-label": "legal-name-input" }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? ""}
                    />
                  )}
                />
              </Grid>
            )}
            {showTradingName && (
              <Grid item xs={12}>
                <Controller
                  name="tradingName"
                  control={control}
                  rules={tradingNameRules}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      label={translate("profile.create.tradingName")}
                      placeholder={String(
                        translate("profile.create.enterTradingName")
                      )}
                      size="small"
                      fullWidth
                      required
                      inputProps={{ "aria-label": "trading-name-input" }}
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? ""}
                    />
                  )}
                />
              </Grid>
            )}
            {showLinkTarget && showLegalName && (
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  {translate(linkLabelKey)}
                </Typography>
                <RowDisplayRadioGroup
                  row
                  value={defaultLinkTarget}
                  sx={{ display: "flex", gap: "3rem", mt: 0.5 }}
                >
                  {showIndividualLinkTarget && (
                    <FormControlLabel
                      value="individual"
                      control={
                        <Radio
                          disabled={!isIndividualSearch}
                          inputProps={{ "aria-label": "Individual" }}
                        />
                      }
                      label={translate("profile.create.individual")}
                    />
                  )}
                  {showBusinessLinkTarget && (
                    <FormControlLabel
                      value="business"
                      control={
                        <Radio
                          disabled={isIndividualSearch}
                          inputProps={{ "aria-label": "Business radio" }}
                        />
                      }
                      label={translate("profile.create.business")}
                    />
                  )}
                </RowDisplayRadioGroup>
                {isCreateDisabled && (
                  <ErrorTypography
                    aria-label="selected type invalid"
                    variant="body1"
                    sx={{ mt: 1 }}
                  >
                    {translate(linkErrorKey)}
                  </ErrorTypography>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ padding: "8px 18px 14px !important" }}>
          <Button
            type="button"
            variant="grey"
            onClick={handleClose}
            aria-label="cancel"
          >
            {translate("common:cancel")}
          </Button>
          <Button
            type="submit"
            variant="success"
            aria-label="confirm"
            disabled={isCreateDisabled}
          >
            {translate("profile.create.createProfileBtn")}
          </Button>
        </DialogActions>
      </form>
      <BackdropLoader isOpen={isLoading} />
    </Dialog>
  );
};

export default CreateProfileModal;
